import { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActiveButton,
  ActiveChip,
  CancelButton,
  CardBody,
  CardHeader,
  CardTitle,
  EnterpriseText,
  ExpiredChip,
  OriginalPrice,
  PopularRibbon,
  PriceBlock,
  PriceCaption,
  PriceRow,
  PriceText,
  PrimaryButton,
  SavingsLine,
  StyledAreaBox,
  StyledChip,
  StyledColoredBox,
  StyledList,
  StyledListItem,
} from '@pages/Pricing/styled';
import styled, { keyframes } from 'styled-components';

import { useTheme } from '@/components/theme-v2/ThemeContext';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const AnimatedCard = styled(StyledAreaBox)<{ $delay?: number; $isDark?: boolean }>`
  animation: ${fadeInUp} 0.5s ease-out;
  animation-delay: ${props => props.$delay || 0}s;
  animation-fill-mode: both;
`;

// Icons
const CheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const CancelIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <path d="M15 9l-6 6M9 9l6 6" />
  </svg>
);

interface PlanCardProps {
  isActive: boolean;
  isExpired?: boolean;
  isPopular?: boolean;
  isFree?: boolean;
  isEnterprise?: boolean;
  tokensInPackage?: number;
  title: string;
  subtitle: string;
  packagePriceTitle: string;
  packagePriceSubtitle?: string;
  originalPriceTitle?: string;
  annualSavings?: string;
  onClick: () => void;
  onCancelClick: ((e: MouseEvent) => void) | (() => void);
  isLoading?: boolean;
  packageButtonLocale?: string;
  animationDelay?: number;
}

const PlanCard: FC<PlanCardProps> = ({
  title,
  subtitle,
  isActive,
  packagePriceTitle,
  packagePriceSubtitle,
  originalPriceTitle,
  annualSavings,
  onClick,
  isPopular = false,
  isFree = false,
  isEnterprise = false,
  isExpired = false,
  tokensInPackage = 0,
  isLoading,
  packageButtonLocale = '',
  onCancelClick,
  animationDelay = 0,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  const tokensPerMonth = `${tokensInPackage.toLocaleString()} ${isFree ? t('billing.tokens') : t('billing.tokensPerMonth')}`;
  const subtitleText = t(subtitle)
    .trim()
    .split('; ')
    .map(desc => desc.replace(';', ''));
  const freeConstraint = isActive && isFree;

  // Convert bgColor from MUI colors to a subtle background - theme aware
  const getCardBackground = () => {
    if (isDarkMode) {
      if (isActive) return 'rgba(0, 255, 127, 0.05)';
      if (isPopular) return 'rgba(0, 200, 224, 0.08)';
      return 'rgba(42, 53, 68, 0.3)';
    }
    if (isActive) return 'rgba(0, 200, 127, 0.08)';
    if (isPopular) return 'rgba(0, 200, 224, 0.06)';
    return 'rgba(0, 0, 0, 0.02)';
  };

  return (
    <AnimatedCard
      $active={isActive && !isExpired}
      $popular={isPopular && !isActive}
      $delay={animationDelay}
      $isDark={isDarkMode}
    >
      <StyledColoredBox $bgColor={getCardBackground()} $isDark={isDarkMode} $isPopular={isPopular && !isActive}>
        <CardHeader>
          <CardTitle $isDark={isDarkMode}>{t(title)}</CardTitle>
          {isExpired && <ExpiredChip>{t('billing.other.expiredChip')}</ExpiredChip>}
          {isActive && !isExpired && <ActiveChip>{t('billing.other.activeChip')}</ActiveChip>}
          {isPopular && !isActive && <PopularRibbon />}
        </CardHeader>

        <StyledList>
          {subtitleText.map(item => (
            <StyledListItem key={item} $isDark={isDarkMode}>
              {item}
            </StyledListItem>
          ))}
        </StyledList>
      </StyledColoredBox>

      <CardBody $isDark={isDarkMode}>
        <PriceRow>
          <PriceBlock>
            <PriceText $isDark={isDarkMode}>{t(packagePriceTitle)}</PriceText>
            {originalPriceTitle && <OriginalPrice $isDark={isDarkMode}>{originalPriceTitle}</OriginalPrice>}
            {packagePriceSubtitle && <PriceCaption $isDark={isDarkMode}>{t(packagePriceSubtitle)}</PriceCaption>}
            {annualSavings && <SavingsLine $isDark={isDarkMode}>{annualSavings}</SavingsLine>}
          </PriceBlock>
          {isActive && !isFree && (
            <CancelButton
              onClick={e => (onCancelClick as (e: unknown) => void)(e)}
              disabled={isLoading}
              title={t('billing.other.cancelPlan')}
            >
              <CancelIcon />
            </CancelButton>
          )}
        </PriceRow>

        {(isFree || tokensInPackage > 0) && <StyledChip $isDark={isDarkMode}>{tokensPerMonth}</StyledChip>}

        {isEnterprise && <EnterpriseText $isDark={isDarkMode}>{t('billing.other.enterpriseText')}</EnterpriseText>}

        {freeConstraint && (
          <ActiveButton>
            <CheckIcon />
            {t('billing.other.activeChip')}
          </ActiveButton>
        )}

        {!isFree && (
          <PrimaryButton
            onClick={onClick}
            disabled={isLoading}
            $disabled={isLoading}
            $isPopular={isPopular && !isActive}
          >
            {isActive ? t('billing.other.topUpText') : t(packageButtonLocale)}
          </PrimaryButton>
        )}

        {isFree && !isActive && (
          // Public-visitor variant: the free trial card shows a CTA that
          // routes to signup (configured by the parent via `onClick`).
          <PrimaryButton onClick={onClick} disabled={isLoading} $disabled={isLoading} $isPopular={false}>
            {t(packageButtonLocale || 'billing.other.getStarted')}
          </PrimaryButton>
        )}
      </CardBody>
    </AnimatedCard>
  );
};

export default memo(PlanCard);
